// @figma file="components-web" component="MobileListCard"
import { MoreHorizontal } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { useMediaQuery } from '../../hooks/use-media-query';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface SwipeAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** Tailwind bg class for the action button. Defaults to bg-primary. */
  className?: string;
  /** When true, styles this action as destructive in the dropdown menu variant. */
  destructive?: boolean;
}

export interface MobileListCardProps {
  onClick?: () => void;
  'aria-label'?: string;
  className?: string;
  /** Applied to the outermost wrapper element. Use `h-full` here when the card lives in a grid cell. */
  containerClassName?: string;
  children: ReactNode;
  /** Actions revealed when the user swipes left (appear on the right). */
  swipeActions?: SwipeAction[];
  /** Actions revealed when the user swipes right (appear on the left). */
  swipeRightActions?: SwipeAction[];
  /**
   * Desktop action presentation.
   * 'buttons' (default) — individual coloured icon buttons on hover.
   * 'menu' — single MoreHorizontal icon that opens a dropdown on hover.
   */
  desktopActionsMode?: 'buttons' | 'menu';
}

const BASE = 'flex w-full items-start gap-3 rounded-xl border bg-card p-3';
const CLICKABLE = 'text-left transition-colors hover:bg-muted cursor-pointer';

const ACTION_WIDTH = 72; // px per action

export function MobileListCard({
  onClick,
  'aria-label': ariaLabel,
  className,
  containerClassName,
  children,
  swipeActions,
  swipeRightActions,
  desktopActionsMode = 'buttons',
}: MobileListCardProps) {
  // Fine pointer (mouse/trackpad) + hover capability → desktop interaction model.
  // Coarse pointer (finger) → swipe interaction model.
  // Reactive: updates when the user plugs/unplugs a mouse on a hybrid device.
  const isDesktop = useMediaQuery('(hover: hover) and (pointer: fine)');

  // ─── Desktop path ─────────────────────────────────────────────────────────
  if (isDesktop) {
    const allActions = [...(swipeRightActions ?? []), ...(swipeActions ?? [])];
    const hasActions = allActions.length > 0;

    const cardCls = cn(BASE, onClick ? CLICKABLE : '', 'relative', className);

    const actionOverlay = hasActions ? (
      desktopActionsMode === 'menu' ? (
        <div className="absolute inset-y-0 right-2 flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Actions"
                onClick={(e) => e.stopPropagation()}
                className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted transition-colors"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[130px] max-lg:rounded-[min(var(--radius),0.75rem)] max-lg:border max-lg:border-border max-lg:p-1 max-lg:shadow-md max-lg:backdrop-blur-none max-lg:bg-popover"
            >
              {allActions.map((action) => (
                <DropdownMenuItem
                  key={action.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                  className={cn(
                    'max-lg:text-sm max-lg:py-1.5 max-lg:px-2 max-lg:gap-2 max-lg:rounded-sm max-lg:border-0',
                    action.destructive ? 'text-destructive focus:text-destructive' : '',
                  )}
                >
                  {action.icon}
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
          {allActions.map((action) => (
            <button
              key={action.label}
              type="button"
              aria-label={action.label}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={cn(
                'flex size-7 cursor-pointer items-center justify-center rounded-md text-white transition-opacity',
                action.className ?? 'bg-primary',
              )}
            >
              {action.icon}
            </button>
          ))}
        </div>
      )
    ) : null;

    return (
      <div className={cn('group relative', containerClassName)}>
        {onClick ? (
          <button type="button" aria-label={ariaLabel} className={cardCls} onClick={onClick}>
            {children}
          </button>
        ) : (
          <div className={cardCls}>{children}</div>
        )}
        {actionOverlay}
      </div>
    );
  }

  // ─── Mobile / touch path ──────────────────────────────────────────────────

  const [offset, setOffset] = useState(0);
  const [snapping, setSnapping] = useState(false);

  // Refs avoid stale closures across touch callbacks
  const offsetRef = useRef(0);
  const startX = useRef(0);
  const startOffset = useRef(0);
  const isDragging = useRef(false);

  const maxLeftReveal = swipeActions ? swipeActions.length * ACTION_WIDTH : 0;
  const maxRightReveal = swipeRightActions ? swipeRightActions.length * ACTION_WIDTH : 0;
  const hasSwipe = !!(swipeActions?.length || swipeRightActions?.length);

  function setOffsetSync(v: number) {
    offsetRef.current = v;
    setOffset(v);
  }

  function handleTouchStart(e: React.TouchEvent) {
    if (!hasSwipe) return;
    startX.current = e.touches[0].clientX;
    startOffset.current = offsetRef.current;
    isDragging.current = true;
    setSnapping(false);
  }

  function handleTouchMove(e: React.TouchEvent) {
    const dx = e.touches[0].clientX - startX.current;
    if (!hasSwipe || !isDragging.current) return;
    const next = startOffset.current + dx;
    const clamped = Math.max(-maxLeftReveal - 12, Math.min(maxRightReveal + 12, next));
    setOffsetSync(clamped);
  }

  function handleTouchEnd() {
    if (!hasSwipe || !isDragging.current) return;
    isDragging.current = false;
    setSnapping(true);
    const cur = offsetRef.current;
    if (cur < -maxLeftReveal / 2) setOffsetSync(-maxLeftReveal);
    else if (cur > maxRightReveal / 2) setOffsetSync(maxRightReveal);
    else setOffsetSync(0);
  }

  function close() {
    if (offsetRef.current !== 0) {
      setSnapping(true);
      setOffsetSync(0);
    }
  }

  function handleActionClick(e: React.MouseEvent, action: SwipeAction) {
    e.stopPropagation();
    setSnapping(true);
    setOffsetSync(0);
    action.onClick();
  }

  const style = hasSwipe
    ? {
        transform: `translateX(${offset}px)`,
        transition: snapping ? 'transform 0.2s ease' : 'none',
      }
    : undefined;

  const inner = onClick ? (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(BASE, CLICKABLE, 'relative z-10 w-full', className)}
      style={style}
      onClick={() => {
        if (offsetRef.current === 0) onClick();
        else close();
      }}
    >
      {children}
    </button>
  ) : (
    <div className={cn(BASE, 'relative z-10', className)} style={style}>
      {children}
    </div>
  );

  if (!hasSwipe) return inner;

  return (
    // touch-pan-y lets the browser handle vertical scrolling;
    // we take over only on horizontal swipes.
    <div
      className="relative overflow-hidden rounded-xl touch-pan-y"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Right-swipe actions — revealed on the left */}
      {swipeRightActions?.length ? (
        <div className="absolute inset-y-0 left-0 flex">
          {swipeRightActions.map((action) => (
            <button
              key={action.label}
              type="button"
              style={{ width: ACTION_WIDTH }}
              className={cn(
                'flex shrink-0 flex-col items-center justify-center gap-1 text-xs font-medium text-white',
                action.className ?? 'bg-primary',
              )}
              onClick={(e) => handleActionClick(e, action)}
              aria-label={action.label}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {inner}

      {/* Left-swipe actions — revealed on the right */}
      {swipeActions?.length ? (
        <div className="absolute inset-y-0 right-0 flex">
          {swipeActions.map((action) => (
            <button
              key={action.label}
              type="button"
              style={{ width: ACTION_WIDTH }}
              className={cn(
                'flex shrink-0 flex-col items-center justify-center gap-1 text-xs font-medium text-white',
                action.className ?? 'bg-primary',
              )}
              onClick={(e) => handleActionClick(e, action)}
              aria-label={action.label}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
